
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { useSpotOwner } from "@/hooks/useSpotOwner";
import FeedbackForm from "@/components/feedback/FeedbackForm";
import BookingDetailsDialog from "./BookingDetailsDialog";
import CancelBookingDialog from "./CancelBookingDialog";
import OvertimeInfo from "./OvertimeInfo";
import { OvertimePayment } from "./OvertimePayment";
import { TimeRemaining } from "./TimeRemaining";
import { DateTimeDisplay } from "./DateTimeDisplay";
import EtaShareButton from "./EtaShareButton";
import CheckInOutButton from "./CheckInOutButton";
import { Navigation } from "lucide-react";
import { openDirections } from "@/lib/directions";

interface BookingCardProps {
  booking: {
    id: string;
    start_time: string;
    end_time: string;
    status: string;
    lot_count: number;
    parking_spot: {
      id?: string;
      address: string;
      owner_name: string;
      owner_id: string;
      latitude?: number | null;
      longitude?: number | null;
    };
    overtime_hours: number;
    overtime_charges: number;
    actual_end_time: string | null;
    overtime_payment_status?: string;
    checked_in_at?: string | null;
    checked_out_at?: string | null;
    user?: {
      first_name?: string;
      last_name?: string;
      phone?: string;
    };
  };
  onCancel: () => void;
  onVacate: (bookingId: string) => void;
}

const BookingCard = ({ booking, onCancel, onVacate }: BookingCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { isOwner } = useSpotOwner(booking.parking_spot.owner_id);

  const canRequestCancel = () => {
    const startTime = new Date(booking.start_time);
    const now = new Date();
    const hoursDifference = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursDifference > 1 && booking.status !== 'cancelled' && booking.status !== 'completed';
  };

  const canGiveFeedback = () => {
    return booking.status === 'completed';
  };

  return (
    <>
      <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowDetails(true)}>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold">{booking.parking_spot.address}</h3>
              <p className="text-sm text-gray-500">Owner: {booking.parking_spot.owner_name}</p>
              <p className="text-sm text-blue-600">
                <span className="font-medium">Lots booked:</span> {booking.lot_count || 1}
              </p>
            </div>
            <div className="space-y-1">
              <DateTimeDisplay label="Start" dateString={booking.start_time} />
              <DateTimeDisplay label="End" dateString={booking.end_time} />
              <p className="text-sm font-medium">
                Status: <span className="capitalize">{booking.status}</span>
              </p>
              {booking.status === 'confirmed' && (
                <TimeRemaining endTime={booking.end_time} />
              )}
              {booking.actual_end_time && (
                <DateTimeDisplay label="Actual End" dateString={booking.actual_end_time} />
              )}
              {booking.overtime_hours > 0 && (
                <>
                  <OvertimeInfo 
                    hours={booking.overtime_hours}
                    charges={booking.overtime_charges}
                  />
                  {!isOwner && booking.status === 'completed' && booking.overtime_payment_status !== 'paid' && (
                    <OvertimePayment booking={booking} />
                  )}
                </>
              )}
              {isOwner && booking.status === 'confirmed' && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onVacate(booking.id);
                  }}
                  className="mt-2 w-full"
                >
                  Mark as Vacated
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDirections({
                  lat: booking.parking_spot.latitude,
                  lng: booking.parking_spot.longitude,
                  address: booking.parking_spot.address,
                })}
              >
                <Navigation className="h-4 w-4 mr-1" /> Directions
              </Button>
              {!isOwner && booking.status === 'confirmed' && (
                <EtaShareButton
                  bookingId={booking.id}
                  destinationLat={booking.parking_spot.latitude}
                  destinationLng={booking.parking_spot.longitude}
                />
              )}
              {!isOwner && booking.status === 'confirmed' && booking.parking_spot.id && (
                <CheckInOutButton
                  bookingId={booking.id}
                  spotId={booking.parking_spot.id}
                  checkedIn={!!booking.checked_in_at}
                  checkedOut={!!booking.checked_out_at}
                  onUpdated={onCancel}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <BookingDetailsDialog
        booking={booking}
        open={showDetails}
        onOpenChange={setShowDetails}
        onCancelClick={() => setShowCancelDialog(true)}
        onFeedbackClick={() => setShowFeedback(true)}
        canRequestCancel={canRequestCancel()}
        canGiveFeedback={canGiveFeedback()}
      />

      <CancelBookingDialog
        bookingId={booking.id}
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onCancel={onCancel}
      />

      <FeedbackForm
        bookingId={booking.id}
        open={showFeedback}
        onOpenChange={setShowFeedback}
      />
    </>
  );
};

export default BookingCard;
